import { useEffect, useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { Plus, Trash2 } from "lucide-react";

import { StepShell, FormField } from "./StepShell";

const EMPTY_ASSET = {
  type: "",
  description: "",
  value: "",
  remarks: "",
};

function createLocalId() {
  return `portal-asset-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
}

function normalizeAsset(asset = {}) {
  return {
    ...EMPTY_ASSET,
    ...asset,
    _portalId:
      asset._portalId ||
      asset.id ||
      createLocalId(),
  };
}

function compactAsset(asset) {
  return {
    type: asset.type || undefined,
    description:
      asset.description || undefined,
    value:
      asset.value === ""
        ? undefined
        : asset.value === null
          ? null
          : Number(asset.value),
    remarks:
      asset.remarks || undefined,
  };
}

/*
 * ------------------------------------------------------------------------
 * Validation helpers
 * ------------------------------------------------------------------------
 */

function isFilled(value) {
  return (
    value !== null &&
    value !== undefined &&
    String(value).trim() !== ""
  );
}

function isAssetComplete(asset) {
  if (!asset) {
    return false;
  }

  /*
   * Asset Type and Estimated Value are mandatory.
   *
   * Description and Remarks are intentionally optional.
   */
  return (
    isFilled(asset.type) &&
    isFilled(asset.value)
  );
}

export default function AssetsStep({
  state,
  patch,
  setStep,
}) {
  const [assets, setAssets] = useState(() =>
    Array.isArray(state?.assets)
      ? state.assets.map(normalizeAsset)
      : [],
  );

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssets(
      Array.isArray(state?.assets)
        ? state.assets.map(normalizeAsset)
        : [],
    );
  }, [state?.assets]);

  /*
   * ------------------------------------------------------------------------
   * Add / Remove / Update
   * ------------------------------------------------------------------------
   */

  const add = () => {
    setAssets((current) => [
      ...current,
      {
        ...EMPTY_ASSET,
        _portalId: createLocalId(),
      },
    ]);
  };

  const remove = (id) => {
    setAssets((current) =>
      current.filter(
        (asset) => asset._portalId !== id,
      ),
    );
  };

  const update = (id, field, value) => {
    setAssets((current) =>
      current.map((asset) =>
        asset._portalId === id
          ? {
              ...asset,
              [field]: value,
            }
          : asset,
      ),
    );
  };

  /*
   * ------------------------------------------------------------------------
   * Validation
   * ------------------------------------------------------------------------
   *
   * No assets = valid.
   *
   * If one or more assets have been added, every asset must have:
   *   1. Asset Type
   *   2. Estimated Value
   *
   * Description and Remarks are optional.
   */

  const valid = useMemo(() => {
    if (assets.length === 0) {
      return true;
    }

    return assets.every(isAssetComplete);
  }, [assets]);

  /*
   * ------------------------------------------------------------------------
   * Save & Continue
   * ------------------------------------------------------------------------
   */

  const save = async () => {
    /*
     * Never allow Save & Continue to proceed if:
     * - another save is already running, or
     * - an added asset is incomplete.
     */
    if (saving || !valid) {
      return;
    }

    setSaving(true);

    try {
      await patch(
        "assets",
        assets.map(compactAsset),
      );

      await setStep(6);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepShell
      testId="portal-assets"
      title="Assets"
      subtitle="Declare your assets, including Shares, Mutual Funds, Government Securities, Insurance, Properties and Vehicles. Provide the relevant details and approximate value for each asset."
      onBack={() => setStep(4)}
      onNext={save}
      nextLabel={
        saving
          ? "Saving..."
          : "Save & Continue"
      }
      disableNext={
        saving || !valid
      }
    >
      {assets.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          No assets added.
        </div>
      )}

      <div className="space-y-5">
        {assets.map((asset, index) => {
          const assetTypeMissing =
            !isFilled(asset.type);

          const estimatedValueMissing =
            !isFilled(asset.value);

          return (
            <div
              key={asset._portalId}
              data-testid={`asset-row-${index}`}
              className="rounded-2xl border border-gray-200 p-5 bg-[#F8FAFC]"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-gray-500">
                  Asset #{index + 1}
                </span>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    remove(asset._portalId)
                  }
                  aria-label={`Remove asset ${index + 1}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Asset Type"
                  required
                >
                  <Input
                    value={asset.type}
                    onChange={(event) =>
                      update(
                        asset._portalId,
                        "type",
                        event.target.value,
                      )
                    }
                    required
                    aria-invalid={
                      assetTypeMissing
                    }
                  />
                </FormField>

                <FormField
                  label="Estimated Value"
                  required
                >
                  <Input
                    type="number"
                    min="0"
                    value={asset.value ?? ""}
                    onChange={(event) =>
                      update(
                        asset._portalId,
                        "value",
                        event.target.value,
                      )
                    }
                    required
                    aria-invalid={
                      estimatedValueMissing
                    }
                  />
                </FormField>

                <FormField
                  label="Description"
                  className="md:col-span-2"
                >
                  <Textarea
                    value={
                      asset.description
                    }
                    onChange={(event) =>
                      update(
                        asset._portalId,
                        "description",
                        event.target.value,
                      )
                    }
                    rows={3}
                  />
                </FormField>

                <FormField
                  label="Remarks"
                  className="md:col-span-2"
                >
                  <Textarea
                    value={asset.remarks}
                    onChange={(event) =>
                      update(
                        asset._portalId,
                        "remarks",
                        event.target.value,
                      )
                    }
                    rows={3}
                  />
                </FormField>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={add}
        className="mt-5 rounded-full"
      >
        <Plus className="h-4 w-4 mr-1" />
        Add Asset
      </Button>
    </StepShell>
  );
}